/**
 * Visio-backgrounds management page.
 * Grid with {@link SlidePanel} for create/edit forms and {@link DeleteModal} for removal.
 */

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { ManagementPage } from '@/components/common/ManagementPage';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { toast } from '@/components/ui/toast';
import { usePageActions } from '@/hooks/usePageActions';
import { CreateVisioBackgroundView } from '@/components/visio-backgrounds/CreateVisioBackgroundView';
import { EditVisioBackgroundView } from '@/components/visio-backgrounds/EditVisioBackgroundView';
import { VisioBackgroundsGrid } from '@/components/visio-backgrounds/VisioBackgroundsGrid';
import {
  formatVisioBackgroundDeleteModalItemName,
  getVisioBackgroundCreateTooltip,
  isVisioBackgroundLimitReached,
  visioBackgroundsKeys,
} from '@/components/visio-backgrounds/visio-background-table-utils';
import {
  deleteVisioBackground,
  getVisioBackgroundsAdmin,
  reorderVisioBackgrounds,
} from '@/services/visio-backgrounds.service';
import type { VisioBackground } from '@/types/visio-background.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Visio backgrounds list with create/edit. */
export const VisioBackgroundsManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canDeleteRow } = usePageActions('visio-backgrounds');
  const queryClient = useQueryClient();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editBackgroundId, setEditBackgroundId] = React.useState<string | null>(null);
  const [toDelete, setToDelete] = React.useState<VisioBackground | null>(null);

  const { data: backgrounds = [], isLoading } = useQuery({
    queryKey: visioBackgroundsKeys.all,
    queryFn: getVisioBackgroundsAdmin,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: visioBackgroundsKeys.all });

  const handleMutationError = (error: unknown) => {
    toast.error(getErrorMessage(error));
  };

  const reorderMutation = useMutation({
    mutationFn: reorderVisioBackgrounds,
    onError: (error: unknown) => {
      handleMutationError(error);
      void invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVisioBackground,
    onSuccess: () => {
      toast.success('Arrière-plan supprimé');
      setToDelete(null);
      void invalidate();
    },
    onError: handleMutationError,
  });

  const limitReached = isVisioBackgroundLimitReached(backgrounds.length);

  const handleOpenCreate = () => {
    setEditBackgroundId(null);
    setCreateFormOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateFormOpen(false);
  };

  const handleCreateSuccess = () => {
    setCreateFormOpen(false);
    void invalidate();
  };

  const handleEdit = (id: string) => {
    setCreateFormOpen(false);
    setEditBackgroundId(id);
  };

  const handleCloseEdit = () => {
    setEditBackgroundId(null);
  };

  const handleEditSuccess = () => {
    setEditBackgroundId(null);
    void invalidate();
  };

  const handleReorder = (ids: string[]) => {
    reorderMutation.mutate(ids);
  };

  const handleCloseDelete = () => {
    setToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete.id);
  };

  const createTooltip = getVisioBackgroundCreateTooltip(limitReached);

  function renderCreateAction(): React.ReactNode {
    if (!canCreate) return undefined;
    return (
      <Button
        preset="create-entity"
        onClick={handleOpenCreate}
        disabled={limitReached}
        tooltipText={createTooltip}
      >
        Ajouter un arrière-plan
      </Button>
    );
  }

  const deleteItemName = formatVisioBackgroundDeleteModalItemName(toDelete);

  return (
    <ManagementPage
      title="Arrière-plans visio"
      subtitle="Gérez les images d'arrière-plan que les participants peuvent choisir pendant les visios"
      actions={renderCreateAction()}
    >
      <VisioBackgroundsGrid
        backgrounds={backgrounds}
        isLoading={isLoading}
        canEdit={canEditRow}
        canDelete={canDeleteRow}
        onEdit={handleEdit}
        onDelete={setToDelete}
        onReorder={handleReorder}
      />

      <SlidePanel isOpen={createFormOpen} onClose={handleCloseCreate}>
        {createFormOpen ? (
          <CreateVisioBackgroundView onClose={handleCloseCreate} onSuccess={handleCreateSuccess} />
        ) : null}
      </SlidePanel>

      <SlidePanel isOpen={!!editBackgroundId} onClose={handleCloseEdit}>
        {editBackgroundId ? (
          <EditVisioBackgroundView
            key={editBackgroundId}
            backgroundId={editBackgroundId}
            onClose={handleCloseEdit}
            onSuccess={handleEditSuccess}
          />
        ) : null}
      </SlidePanel>

      <DeleteModal
        open={!!toDelete}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        itemName={deleteItemName}
        isLoading={deleteMutation.isPending}
      />
    </ManagementPage>
  );
};
